import { GroupedInsights } from '@toggle/chart';
import {
  isCatalystInsight,
  isEarningsInsight,
  isInsightFilter,
} from '@toggle/toggle';
import { isToday } from 'date-fns';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '~/views/insights/components/insights-tooltip/InsightsTooltip';
import { CatalystInsightIcon } from '~/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';

import * as S from './ChartInsightIcon.styles';
import { calculateIconPosition } from './utils';

interface ChartInsightIconProps {
  groupedInsights: GroupedInsights;
  insightIconRight: number;
  insightIconTop: number;
  yOffsetCount: number;
  isSimpleChart?: boolean;
}

export const ChartInsightIcon: FC<ChartInsightIconProps> = ({
  groupedInsights,
  insightIconTop,
  insightIconRight,
  yOffsetCount,
  isSimpleChart,
}) => {
  const { t } = useTranslation(['chart']);
  const { openDrawer: openInsightDrawer } = useInsightDrawer();

  const singleIconWidth = isSimpleChart ? 12 : 24;
  const singleIconHeight = isSimpleChart ? 14 : 28;

  const verticalCombinedWidth = isSimpleChart ? 12 : 24;
  const verticalCombinedHeight = isSimpleChart ? 24 : 47;

  return (
    <>
      {groupedInsights.insights.map((insight, idx) => {
        if (isCatalystInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
          });

          return (
            <S.IconContainer key={insight.id} $top={iconY} $right={iconRight}>
              <CatalystInsightIcon
                catalystInsightId={insight.id}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                identifier={insight.identifier}
                source={POSTHOG_TRACKING_SOURCES.CHART}
                isCompanyCatalyst
              />
            </S.IconContainer>
          );
        }

        if (isEarningsInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
          });

          return (
            <S.IconContainer key={insight.id} $top={iconY} $right={iconRight}>
              <EarningsInsightIcon
                entityTag={insight.entity_tag}
                earningsInsight={insight}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                source={POSTHOG_TRACKING_SOURCES.CHART}
              />
            </S.IconContainer>
          );
        }

        if (!isInsightFilter(insight)) {
          return null;
        }

        const isStarred = isToday(new Date(insight.created_at));
        const conditions = getConditionIcons({
          kind: insight.metadata.kind,
          conditions: insight.metadata.conditions,
        });
        const isCombined = conditions.length > 1;
        const iconWidth = isCombined ? verticalCombinedWidth : singleIconWidth;
        const iconHeight = isCombined
          ? verticalCombinedHeight
          : singleIconHeight;

        const { iconY, iconRight } = calculateIconPosition({
          idx,
          iconHeight: singleIconHeight,
          iconWidth: singleIconWidth,
          insightIconTop,
          insightIconRight,
          yOffsetCount,
        });

        const iconSize = {
          width: `${iconWidth}px`,
          height: `${iconHeight}px`,
        };

        const episodeLabel = t('chart:insightCard.episode', {
          count: insight.metadata.episode_count,
        });

        return (
          <InsightsTooltip
            key={insight.id}
            conditions={conditions}
            direction={getExploreDirection(insight.metadata.direction)}
            customSize={{ width: '27px', height: '32px' }}
            isStarred={isToday(new Date(insight.created_at))}
            isMatched
            horizon={insight.metadata.horizon}
            median={insight.metadata.median_return}
            stars={insight.metadata.stars}
            name={insight.card.name}
            entityTag={insight.metadata.entity}
            episodeLabel={episodeLabel}
          >
            <S.IconContainer
              key={insight.id}
              $top={iconY}
              $right={iconRight}
              onClick={() => openInsightDrawer(insight.id, { source: 'chart' })}
            >
              <InsightIconsRoot
                conditions={conditions}
                direction={getExploreDirection(insight.metadata.direction)}
                customSize={iconSize}
                isStarred={isStarred}
                isCombined={isCombined}
                layout="vertical"
                isSparklesCondensed={isSimpleChart}
              />
            </S.IconContainer>
          </InsightsTooltip>
        );
      })}
    </>
  );
};
